import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Button,
    Alert
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    BellIcon
} from "../../anatomy/icons";

const NAME = "Alert";

const USAGE_CODE = `const [isOpen, setIsOpen] = useState(true);

if(!isOpen) {
    return null;
}

return <Alert
    actions={[
        {
            title: "Details",
            onPress: handleDetails
        }
    ]}
    description="The system will be briefly unavailable on Saturday."
    onClose={() => setIsOpen(false)}
    title="Maintenance scheduled"
    isClosable={true}
    type="info"
/>;`;

const COMPOSITION_CODE = `<Alert
    announceBehaviour="assertive"
    type="danger"
>
    <Alert.Title
        headingLevel={4}
    >
        The form has 2 errors
    </Alert.Title>
    <Alert.Description>
        Fix the highlighted fields before you continue.
    </Alert.Description>
    <Alert.Actions>
        <Button
            onPress={handleFocusFirstError}
            title="Go to the fields"
            variant="outline"
            type="danger"
            size="small"
        />
    </Alert.Actions>
</Alert>`;

const VARIANTS = [
    "tonal",
    "outline",
    "banner"
] as const;

const TYPES = [
    "neutral",
    "primary",
    "info",
    "success",
    "warning",
    "danger"
] as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("alertPage.typesTitle"),
        id: "types"
    },
    {
        title: t("alertPage.actionsTitle"),
        id: "actions"
    },
    {
        title: t("alertPage.bannerTitle"),
        id: "banner"
    },
    {
        title: t("alertPage.compositionTitle"),
        id: "composition"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const AlertPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isOpen,
        setIsOpen
    ] = useState(true);

    const typeTitles = {
        neutral: t("alertPage.typeNeutral"),
        primary: t("alertPage.typePrimary"),
        success: t("alertPage.typeSuccess"),
        warning: t("alertPage.typeWarning"),
        danger: t("alertPage.typeDanger"),
        info: t("alertPage.typeInfo")
    };

    const handleClose = () => setIsOpen(false);

    const handleShow = () => setIsOpen(true);

    return <Article>
        <ComponentHeader
            lead={t("alertPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("alertPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("alertPage.overviewCaption")}
                isStacked={true}
            >
                {
                    isOpen ?
                        <Alert
                            description={t("alertPage.overviewDescription")}
                            actions={[
                                {
                                    title: t("alertPage.details"),
                                    onPress: () => undefined
                                }
                            ]}
                            title={t("alertPage.overviewTitle")}
                            onClose={handleClose}
                            isClosable={true}
                            type="info"
                        />
                    :
                        <Button
                            title={t("alertPage.showAgain")}
                            onPress={handleShow}
                            variant="outline"
                            type="neutral"
                            size="small"
                        />
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("alertPage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    VARIANTS.map((variant) => <Alert
                        description={t("sample.body")}
                        variant={variant}
                        title={variant}
                        key={variant}
                        type="info"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("alertPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("alertPage.typesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    TYPES.map((type) => <Alert
                        title={typeTitles[type]}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("alertPage.actionsTitle")}
            id="actions"
        >
            <Paragraph>
                {t("alertPage.actionsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Alert
                    description={t("alertPage.trialDescription")}
                    actions={[
                        {
                            title: t("alertPage.viewPlans"),
                            onPress: () => undefined
                        },
                        {
                            title: t("alertPage.remindLater"),
                            onPress: () => undefined
                        }
                    ]}
                    title={t("alertPage.typeWarning")}
                    type="warning"
                />
                <Alert
                    actions={[
                        {
                            title: t("alertPage.open"),
                            onPress: () => undefined
                        }
                    ]}
                    title={t("sample.notification")}
                    icon={BellIcon}
                />
            </Preview>
        </Section>
        <Section
            title={t("alertPage.bannerTitle")}
            id="banner"
        >
            <Paragraph>
                {t("alertPage.bannerText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Alert
                    title={t("alertPage.bannerOffline")}
                    isBanner={true}
                    type="info"
                />
                <Alert
                    description={t("alertPage.trialDescription")}
                    actions={[
                        {
                            title: t("alertPage.viewPlans"),
                            onPress: () => undefined
                        }
                    ]}
                    title={t("alertPage.typeWarning")}
                    variant="outline"
                    isBanner={true}
                    type="warning"
                />
            </Preview>
        </Section>
        <Section
            title={t("alertPage.compositionTitle")}
            id="composition"
        >
            <Paragraph>
                {t("alertPage.compositionText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Alert
                    announceBehaviour="assertive"
                    type="danger"
                >
                    <Alert.Title
                        headingLevel={4}
                    >
                        {t("alertPage.formErrors")}
                    </Alert.Title>
                    <Alert.Description>
                        {t("alertPage.formErrorsDescription")}
                    </Alert.Description>
                    <Alert.Actions>
                        <Button
                            title={t("alertPage.goToFields")}
                            onPress={() => undefined}
                            variant="outline"
                            type="danger"
                            size="small"
                        />
                    </Alert.Actions>
                </Alert>
            </Preview>
            <CodeBlock
                code={COMPOSITION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("alertPage.a11yRole"),
                    t("alertPage.a11yAnnounce"),
                    t("alertPage.a11yIos"),
                    t("alertPage.a11yIcon"),
                    t("alertPage.a11yKeyboard"),
                    t("alertPage.a11yHeading"),
                    t("alertPage.a11yMotion"),
                    t("alertPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"neutral\" | \"primary\" | \"info\" | \"success\" | \"warning\" | \"danger\"",
                        description: t("alertPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"tonal\" | \"outline\" | \"banner\"",
                        description: t("alertPage.propVariant"),
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        description: t("alertPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("alertPage.propDescription"),
                        name: "description",
                        type: "ReactNode"
                    },
                    {
                        description: t("alertPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "Array<Omit<ButtonProps, \"variant\" | \"size\" | \"type\">>",
                        description: t("alertPage.propActions"),
                        name: "actions"
                    },
                    {
                        description: t("alertPage.propActionPlacement"),
                        type: "\"bottom\" | \"end\"",
                        defaultValue: "\"bottom\"",
                        name: "actionPlacement"
                    },
                    {
                        description: t("alertPage.propIsClosable"),
                        defaultValue: "false",
                        name: "isClosable",
                        type: "boolean"
                    },
                    {
                        description: t("alertPage.propOnClose"),
                        type: "() => void",
                        name: "onClose"
                    },
                    {
                        description: t("alertPage.propIsBanner"),
                        defaultValue: "false",
                        name: "isBanner",
                        type: "boolean"
                    },
                    {
                        description: t("alertPage.propAnnounceBehaviour"),
                        type: "\"none\" | \"polite\" | \"assertive\"",
                        name: "announceBehaviour"
                    },
                    {
                        description: t("alertPage.propRole"),
                        type: "Role",
                        name: "role"
                    },
                    {
                        type: "\"off\" | \"polite\" | \"assertive\"",
                        description: t("alertPage.propAriaLive"),
                        name: "aria-live"
                    },
                    {
                        description: t("alertPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("alertPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("alertPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("alertPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Alert.Title
                </InlineCode>
                {" "}
                {t("alertPage.partTitleText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("alertPage.propHeadingLevel"),
                        type: "2 | 3 | 4 | 5 | 6",
                        name: "headingLevel"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("alertPage.propPartCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("alertPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default AlertPage;
